import { Checkbox, Typography } from 'antd';
import type { QueryClient } from '@tanstack/react-query';
import type { TenantOrder } from '@/mocks/orders';
import { orderKeys } from '@/services/queryKeys';
import { orderService, type ConfirmOrderVariables } from '@/services/order.service';

type PickupOrderFields = Pick<TenantOrder, 'channel' | 'platform' | 'delivery_method' | 'shipping'>;

export function isPickupHandoverOnConfirm(order: PickupOrderFields | null | undefined): boolean {
  return order?.channel === 'POS'
    && order.platform === 'offline'
    && (order.delivery_method ?? order.shipping?.method) === 'pickup';
}

export function hasPersistedPickupHandover(order: TenantOrder | null | undefined): boolean {
  return isPickupHandoverOnConfirm(order)
    && Boolean(order?.pos_fulfillment?.events?.some((event) => event.event_type === 'customer_handover_confirmed'));
}

export function getOrderOutstandingDebt(order: Pick<TenantOrder, 'total_amount' | 'paid_amount'>): number {
  return Math.max(0, Number(order.total_amount || 0) - Number(order.paid_amount || 0));
}

export function canConfirmPickupOrder(outstandingDebt: number, debtAcknowledged: boolean): boolean {
  return outstandingDebt <= 0 || debtAcknowledged;
}

export function refetchConfirmedOrderDetail(queryClient: QueryClient, orderId: string): Promise<TenantOrder> {
  return queryClient.fetchQuery({
    queryKey: orderKeys.detail(orderId),
    queryFn: () => orderService.getById(orderId),
    staleTime: 0,
  });
}

export async function confirmOrderAndRefreshStatus(
  isPickup: boolean,
  confirm: () => Promise<unknown>,
  refetch: () => Promise<TenantOrder>,
): Promise<{ refreshedOrder?: TenantOrder; refreshFailed: boolean }> {
  await confirm();
  if (!isPickup) return { refreshFailed: false };
  try {
    return { refreshedOrder: await refetch(), refreshFailed: false };
  } catch {
    return { refreshFailed: true };
  }
}

export function buildPickupOrderConfirmVariables(
  order: Pick<TenantOrder, '_id' | 'total_amount' | 'paid_amount' | 'channel' | 'platform' | 'delivery_method' | 'shipping'>,
  debtAcknowledged: boolean,
): ConfirmOrderVariables {
  const needsAcknowledgement = isPickupHandoverOnConfirm(order) && getOrderOutstandingDebt(order) > 0;
  return {
    id: order._id,
    ...(needsAcknowledgement && debtAcknowledged ? { confirm_outstanding_debt: true } : {}),
  };
}

interface PickupOrderConfirmationContentProps {
  prompt: string;
  isPickupHandoverOnConfirm: boolean;
  outstandingDebt: number;
  onAcknowledge: (acknowledged: boolean) => void;
}

export function PickupOrderConfirmationContent({
  prompt,
  isPickupHandoverOnConfirm: isPickup,
  outstandingDebt,
  onAcknowledge,
}: PickupOrderConfirmationContentProps): JSX.Element {
  return (
    <div>
      <Typography.Paragraph>{prompt}</Typography.Paragraph>
      {isPickup && (
        <>
          <Typography.Paragraph>Duyệt đơn cũng ghi nhận khách đã nhận hàng tại chi nhánh.</Typography.Paragraph>
          {outstandingDebt > 0 && (
            <Checkbox onChange={(event) => onAcknowledge(event.target.checked)}>
              Tôi xác nhận khách đồng ý ghi nhận công nợ {outstandingDebt.toLocaleString('vi-VN')} đ.
            </Checkbox>
          )}
        </>
      )}
    </div>
  );
}
